import { motion } from "framer-motion";
import { ASSETS, CLINIC } from "@/lib/assets";

const ease = [0.76, 0, 0.24, 1];

const RevealText = ({ children, delay = 0, className = "" }) => (
    <span className="mask-line inline-block">
        <motion.span
            className={`inline-block ${className}`}
            initial={{ y: "110%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 1, delay, ease }}
        >
            {children}
        </motion.span>
    </span>
);

export const DoctorChapter = () => {
    return (
        <section
            id="doctor"
            className="py-20 md:py-32 lg:py-40 px-5 md:px-8 lg:px-10 relative"
            data-testid="doctor-section"
        >
            <div className="mx-auto max-w-[1600px] grid grid-cols-12 gap-6 md:gap-8 lg:gap-10">
                {/* Chapter number */}
                <div className="col-span-12 md:col-span-2 flex md:block items-end justify-between mb-2 md:mb-0">
                    <div>
                        <div className="label text-surya-muted">Chapter</div>
                        <div className="chapter-num text-[3rem] md:text-[4rem] lg:text-[4.5rem] leading-none text-surya-ink mt-1">
                            01<span className="text-surya-orange">.</span>
                        </div>
                        <div className="label text-surya-muted md:mt-2">The Doctor</div>
                    </div>
                </div>

                {/* Portrait */}
                <div className="col-span-12 md:col-span-4 relative">
                    <motion.div
                        initial={{ clipPath: "inset(15% 10% 15% 10%)", scale: 1.1 }}
                        whileInView={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
                        viewport={{ once: true, margin: "-8%" }}
                        transition={{ duration: 1.3, ease }}
                        className="relative aspect-[4/5] md:aspect-[3/4] w-full overflow-hidden bg-surya-surface border border-surya-line"
                    >
                        <img
                            src={ASSETS.doctor}
                            alt="Dr. Shikha Sinha at Surya Dental Care"
                            className="editorial-img absolute inset-0 h-full w-full object-cover"
                            data-testid="doctor-portrait"
                            loading="lazy"
                            decoding="async"
                        />
                    </motion.div>
                    <div className="mt-3 md:mt-4 flex items-center justify-between label text-surya-muted text-[0.6rem] md:text-xs">
                        <span>Fig. 01</span>
                        <span className="text-right">
                            {CLINIC.doctor} · {CLINIC.creds}
                        </span>
                    </div>
                </div>

                {/* Manifesto */}
                <div className="col-span-12 md:col-span-6 md:pl-4 lg:pl-8 mt-4 md:mt-0">
                    <div className="label text-surya-orange mb-4 md:mb-6 lg:mb-8">
                        On precision, patience, and people.
                    </div>
                    <h2
                        className="font-serif text-surya-ink text-[clamp(1.8rem,4.4vw,4.5rem)] leading-[0.98] tracking-[-0.02em] font-light"
                        data-testid="doctor-manifesto"
                    >
                        <RevealText>Beyond the clinical,</RevealText>{" "}
                        <RevealText delay={0.08}>
                            <span className="italic-display text-surya-orange">we build</span>
                        </RevealText>{" "}
                        <RevealText delay={0.16}>trust — one</RevealText>{" "}
                        <RevealText delay={0.24}>quiet, considered</RevealText>{" "}
                        <RevealText delay={0.32}>visit at a time.</RevealText>
                    </h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-8%" }}
                        transition={{ duration: 1, delay: 0.25, ease }}
                        className="mt-6 md:mt-10 text-surya-inkSoft text-sm md:text-base lg:text-lg leading-relaxed max-w-2xl"
                    >
                        A BDS graduate of 2016 and MDS in Periodontology &amp;
                        Oral Implantology, Dr. Sinha serves as a{" "}
                        <span className="text-surya-ink">
                            CMP Dental Surgeon at the Divisional Railway Hospital,
                            Hatia
                        </span>{" "}
                        alongside private practice at Surya Dental Care — Dibdih,
                        Ranchi. She treats teeth the way an editor treats a
                        manuscript: every detail purposeful, every intervention
                        considered.
                    </motion.p>
                </div>

                {/* Timeline — appears below on all screens */}
                <div
                    className="col-span-12 mt-10 md:mt-16 lg:mt-20"
                    data-testid="doctor-timeline"
                >
                    <div className="grid grid-cols-12 gap-6 md:gap-8 lg:gap-10">
                        <div className="col-span-12 md:col-span-3">
                            <div className="label text-surya-orange mb-3">
                                The Journey
                            </div>
                            <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl text-surya-ink font-light leading-tight tracking-[-0.01em]">
                                A decade,{" "}
                                <span className="italic-display">
                                    quietly earned.
                                </span>
                            </h3>
                        </div>
                        <div className="col-span-12 md:col-span-9">
                            <div className="border-t border-surya-line">
                                {CLINIC.timeline.map((t, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-8%" }}
                                        transition={{ duration: 0.9, delay: i * 0.08, ease }}
                                        className="grid grid-cols-12 gap-3 md:gap-6 items-start border-b border-surya-line py-5 md:py-7 group"
                                        data-testid={`timeline-${i}`}
                                    >
                                        <div className="col-span-4 md:col-span-3">
                                            <div className="label text-surya-muted text-[0.65rem] md:text-xs">
                                                {t.year}
                                            </div>
                                        </div>
                                        <div className="col-span-8 md:col-span-4">
                                            <div className="font-serif text-xl md:text-2xl lg:text-3xl text-surya-ink font-light leading-tight tracking-[-0.01em] group-hover:text-surya-orange transition-colors">
                                                {t.title}
                                            </div>
                                        </div>
                                        <div className="col-span-12 md:col-span-5 text-surya-inkSoft text-sm md:text-base leading-relaxed">
                                            {t.body}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
